import cn from 'classnames';
import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import DocumentText from 'components/Shared/Icons/DocumentText';
import NewWindow from 'components/Shared/Icons/NewWindow';
import Checkbox from 'components/Shared/Inputs/Checkbox';
import CpPopover from 'components/Shared/Popover/CpPopover';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { reformatDateFromMMDDYYtoDDMMYY } from 'shared/dates';
import { colors, fontSize } from 'styles/cp';
import { UnpaidInvoice } from 'types/Insight';

const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/BatchPayments/BatchInvoiceDataRow';

interface BatchInvoiceDataRowProps {
  invoice: UnpaidInvoice;
  index: number;
  handleCheckboxChange: (invoice: UnpaidInvoice) => void;
  handleShowToolTip: (docNumber: string, show: boolean) => void;
  tooltipStates: Record<string, boolean>;
  handleDocumentClick: (invoice: UnpaidInvoice) => void;
  handleNewWindowClick: (link: string) => void;
  currency: string;
  disabled?: boolean;
  maxSelectableBalance: number;
  partialPaymentAmount?: number | null;
}

const BatchInvoiceDataRow = ({
  invoice,
  index,
  handleCheckboxChange,
  handleShowToolTip,
  tooltipStates,
  handleDocumentClick,
  handleNewWindowClick,
  currency,
  disabled = false,
  maxSelectableBalance,
  partialPaymentAmount = null,
}: BatchInvoiceDataRowProps) => {
  const hasPartialPayment = invoice.defaultInvoice && partialPaymentAmount !== null;

  const displayNumber = invoice.docNumber || invoice.saleId || '';

  const tooltipId = useMemo(() => {
    const originalTooltipId =
      (invoice.saleId && `sale-${invoice.saleId}`) ||
      (invoice.docNumber && `doc-${invoice.docNumber}`) ||
      (invoice.link && `link-${invoice.link}`) ||
      '';

    const sanitizedTooltipId = originalTooltipId.replace(/[^a-zA-Z0-9_-]/g, '-');

    if (sanitizedTooltipId !== originalTooltipId) {
      splunkReporter.contextual({
        event: 'batchPayment',
        action: 'tooltipIdSanitized' as any,
        logInfo: { logLevel: 'error', logger },
        activityInfo: {
          message:
            'Prevented querySelectorAll crash - tooltip ID contained invalid CSS selector characters',
          originalTooltipId,
          sanitizedTooltipId,
          docNumber: invoice.docNumber,
          saleId: invoice.saleId,
          invoiceIndex: index,
          errorType: 'querySelectorAll_invalid_selector',
          balance: invoice.balance,
          dueDate: invoice.dueDate,
        },
      });
    }

    return sanitizedTooltipId;
  }, [invoice.docNumber, invoice.saleId, invoice.link, invoice.balance, invoice.dueDate, index]);

  const safeHandleShowToolTip = (show: boolean) => {
    if (tooltipId) {
      handleShowToolTip(tooltipId, show);
    }
  };

  return (
    <div
      key={invoice.docNumber || invoice.saleId || index}
      className="invoice-row-wrapper data-row"
    >
      <div className="checkbox-column">
        <Checkbox
          checked={invoice.selected || false}
          onChange={() => handleCheckboxChange(invoice)}
          disabled={disabled || (!invoice.defaultInvoice && invoice.balance > maxSelectableBalance)}
          type="primary"
        />
      </div>

      <div className="invoice-number-wrapper">
        <span
          className={cn('invoice-data', 'invoice-number', {
            'has-badge': invoice.defaultInvoice,
          })}
        >
          {displayNumber}
        </span>
        {invoice.defaultInvoice && (
          <span className="invoice-default-text">
            <FormattedMessage id="Viewing" defaultMessage="Viewing" />
          </span>
        )}
      </div>

      <div className="section-wrapper due-date-section">
        <div className="due-date-info">
          <span className="invoice-data">{reformatDateFromMMDDYYtoDDMMYY(invoice.dueDate)}</span>
          {invoice.overDue && (
            <>
              <span
                className="attention-icon"
                id={tooltipId ? `attention-${tooltipId}` : undefined}
                onMouseEnter={() => safeHandleShowToolTip(true)}
                onMouseLeave={() => safeHandleShowToolTip(false)}
              >
                !
              </span>
              {tooltipId && (
                <CpPopover
                  innerClassName="cp-tooltip"
                  placement="top"
                  target={`attention-${tooltipId}`}
                  isOpen={tooltipStates[tooltipId] || false}
                  showArrow={true}
                  modifiers={{
                    offset: {
                      enabled: true,
                      offset: '0, 13px',
                    },
                  }}
                >
                  <span>
                    <FormattedMessage id="OVERDUE" defaultMessage="Overdue" />
                  </span>
                </CpPopover>
              )}
            </>
          )}
        </div>
      </div>

      <div className="section-wrapper amount-section">
        <div className="amount-container">
          <span className="invoice-data">
            <FormattedNumber value={invoice.balance} style="currency" currency={currency} />
          </span>
          {invoice.defaultInvoice && partialPaymentAmount !== null && (
            <span className="partial-payment-text">
              <FormattedMessage id="PARTIAL_PAYMENT" defaultMessage="Partial:" />{' '}
              <FormattedNumber value={partialPaymentAmount} style="currency" currency={currency} />
            </span>
          )}
        </div>
      </div>

      {invoice.defaultInvoice ? (
        <button
          className="menu-button"
          onClick={() => handleDocumentClick(invoice)}
          aria-label="View invoice document"
        >
          <DocumentText color={colors.gray02} />
        </button>
      ) : (
        <button
          className="menu-button"
          onClick={() => handleNewWindowClick(invoice.link)}
          aria-label="Open invoice in new window"
        >
          <NewWindow color={colors.gray02} />
        </button>
      )}

      <style jsx>{`
        .invoice-row-wrapper {
          display: flex;
          width: 100%;
          min-height: 40px;
          padding: ${hasPartialPayment ? '10px 12px' : '0 12px'};
          justify-content: space-between;
          align-items: center;
        }

        .data-row {
          background-color: ${colors.white};
          border-bottom: 1px solid ${colors.gray06};
        }

        .data-row:last-child {
          border-bottom: none;
        }

        .checkbox-column {
          width: 46px;
          display: flex;
        }

        .invoice-number-wrapper {
          width: 150px;
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .invoice-number {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex-shrink: 1;
        }

        .invoice-number.has-badge {
          max-width: 80px;
        }

        .invoice-default-text {
          font-family: AvenirNextforINTUIT-Demi;
          font-size: ${fontSize.xxs};
          color: ${colors.gray};
          background-color: ${colors.paleBlue};
          border-radius: 4px;
          padding: 2px 6px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .section-wrapper {
          flex: 1;
        }

        .due-date-section {
          text-align: left;
        }

        .amount-section {
          text-align: right;
        }

        .amount-container {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .partial-payment-text {
          font-family: AvenirNextforINTUIT-Regular;
          font-size: ${fontSize.xxs};
          color: ${colors.gray02};
          margin-top: 2px;
        }

        .invoice-data {
          font-family: AvenirNextforINTUIT-Regular;
          font-size: ${fontSize.xs};
          color: ${colors.gray};
        }

        .due-date-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .attention-icon {
          width: 16px;
          height: 16px;
          background-color: ${colors.orangeWarn3};
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 10px;
          color: white;
          font-weight: bold;
        }

        .menu-button {
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 0 10px 16px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default BatchInvoiceDataRow;
